// ============================================================================
// Intake engine — pure logic over the data-driven question schema.
// Renders nothing; intake-page.tsx is the view. Jack can replace questions.ts
// wholesale and nothing here changes.
// ============================================================================

import {
  INTAKE_TRANCHES,
  type ContactKey,
  type IntakeQuestion,
  type IntakeTranche,
} from './questions';

export type Answers = Record<string, string>;

/** How many answers count against the applicant (shown on the profile; ends nothing — TCSD-0493). */
export function strikeCount(answers: Answers): number {
  let strikes = 0;
  for (const tranche of INTAKE_TRANCHES) {
    for (const q of tranche.questions) {
      const a = answers[q.id];
      if (!a || !q.options) continue;
      const opt = q.options.find((o) => o.value === a);
      if (opt?.strike) strikes++;
    }
  }
  return strikes;
}

/** All required questions in a tranche answered (non-empty)? */
export function trancheComplete(tranche: IntakeTranche, answers: Answers): boolean {
  return tranche.questions.every(
    (q) => !q.required || (answers[q.id] ?? '').trim() !== '',
  );
}

export interface ExtractedSignals {
  owner_operated: boolean | null;
  performs_directly: boolean | null;
  bench_depth: number | null;
}

export interface ExtractedContact {
  business_name: string;
  owner_name: string | null;
  phone: string | null;
  email: string | null;
  market: string | null;
}

/** Map raw answers → the structured fit-signal columns. */
export function extractSignals(answers: Answers): ExtractedSignals {
  const out: ExtractedSignals = {
    owner_operated: null,
    performs_directly: null,
    bench_depth: null,
  };
  for (const tranche of INTAKE_TRANCHES) {
    for (const q of tranche.questions) {
      const a = answers[q.id];
      if (!a || !q.signal || q.signal === 'market') continue;
      if (q.signal === 'bench_depth') {
        const n = q.signalNumber?.[a];
        if (n !== undefined) out.bench_depth = n;
      } else {
        out[q.signal] = (q.signalTrue ?? []).includes(a);
      }
    }
  }
  return out;
}

/** Map raw answers → contact columns + market. */
export function extractContact(answers: Answers): ExtractedContact {
  const byContact = (key: ContactKey): string | null => {
    for (const tranche of INTAKE_TRANCHES) {
      for (const q of tranche.questions) {
        if (q.contact === key) {
          const v = (answers[q.id] ?? '').trim();
          return v || null;
        }
      }
    }
    return null;
  };
  let market: string | null = null;
  for (const tranche of INTAKE_TRANCHES) {
    for (const q of tranche.questions) {
      if (q.signal === 'market') market = (answers[q.id] ?? '').trim() || null;
    }
  }
  return {
    business_name: byContact('business_name') ?? '',
    owner_name: byContact('owner_name'),
    phone: byContact('phone'),
    email: byContact('email'),
    market,
  };
}

/** One active market as the link check delivers it (sourcing_intake_context → markets). */
export interface MarketChoice {
  /** The master list's own name, e.g. "Tampa Metro" — what gets submitted. */
  name: string;
  /** What the applicant reads, e.g. "Tampa". The same in English and Spanish. */
  label: string;
}

/** Read the markets off the link-check answer, ignoring anything malformed. */
export function marketsFromContext(raw: unknown): MarketChoice[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((m) => {
    if (!m || typeof m !== 'object') return [];
    const name = (m as { name?: unknown }).name;
    const label = (m as { label?: unknown }).label;
    if (typeof name !== 'string' || !name.trim()) return [];
    return [{ name: name.trim(), label: typeof label === 'string' && label.trim() ? label.trim() : name.trim() }];
  });
}

/**
 * The form with the area question's cities filled in from the master market
 * list (item 1052, decision 1B). The cities come first, in the list's own
 * order; the schema's fixed options ("Somewhere else") stay at the end. Every
 * other question is returned untouched. The submitted value is the market's
 * own name, which market_from_applicant_answer() accepts as it is.
 */
export function withMarketOptions(tranches: IntakeTranche[], markets: MarketChoice[]): IntakeTranche[] {
  if (markets.length === 0) return tranches;
  return tranches.map((t) => ({
    ...t,
    questions: t.questions.map((q) => {
      if (q.signal !== 'market' || q.type !== 'choice') return q;
      const fixed = q.options ?? [];
      const taken = new Set(fixed.map((o) => o.value));
      const cities = markets
        .filter((m) => !taken.has(m.name))
        .map((m) => ({ value: m.name, label: { en: m.label, es: m.label } }));
      return { ...q, options: [...cities, ...fixed] };
    }),
  }));
}

export function questionById(id: string): IntakeQuestion | undefined {
  for (const tranche of INTAKE_TRANCHES) {
    const q = tranche.questions.find((x) => x.id === id);
    if (q) return q;
  }
  return undefined;
}

/**
 * The inverse of extractContact: turn the prefill a per-prospect token carries
 * back into answers, keyed by the question ids that declare those contact keys.
 *
 * ⛔ The mapping is derived from the question schema, never hard-coded — the
 * same declaration extractContact reads on the way out. Jack can replace
 * questions.ts wholesale and both directions follow.
 */
export function prefillAnswers(prefill: Record<string, unknown> | null | undefined): Answers {
  const out: Answers = {};
  if (!prefill) return out;
  const take = (k: string): string => {
    const v = prefill[k];
    return typeof v === 'string' ? v.trim() : '';
  };
  for (const tranche of INTAKE_TRANCHES) {
    for (const q of tranche.questions) {
      if (q.contact) {
        const v = take(q.contact);
        if (v) out[q.id] = v;
      } else if (q.signal === 'market') {
        const v = take('market');
        // Only prefill a free-text market. A choice question's options are a
        // controlled list and the stored market may not be one of them —
        // writing a value no option carries would look answered and submit
        // something the applicant never picked.
        if (v && q.type !== 'choice') out[q.id] = v;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Answer validation
// ---------------------------------------------------------------------------
// > "REQUIRED" ONLY EVER MEANT "NOT EMPTY", AND THAT IS NOT AN ANSWER. Ben typed
// nine digits into the phone box on 2026-09-11; the form took it, submitted it
// and marked the intake complete. A phone number that cannot be dialled is worse
// than a blank one, because a blank one gets chased and a wrong one looks
// answered.
//
// The rule is chosen by `inputMode`, which the question schema ALREADY declares
// - no new field, and no second list of per-question rules to keep in step. A
// question that declares no inputMode is checked for presence only, as before.
export type AnswerProblem = 'required' | 'phone' | 'email';

/** Digits only. A US number is 10, or 11 when it carries the leading country 1. */
export function phoneLooksComplete(raw: string): boolean {
  const d = raw.replace(/[^0-9]/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
}

/** Deliberately loose: one @, something either side, a dot in the domain. It is
 *  here to catch a typo, not to adjudicate what an address may contain. */
export function emailLooksComplete(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw.trim());
}

/** Every problem on this tranche, keyed by question id. Empty means good to go. */
export function trancheProblems(
  tranche: IntakeTranche,
  answers: Answers,
): Record<string, AnswerProblem> {
  const out: Record<string, AnswerProblem> = {};
  for (const q of tranche.questions) {
    const v = (answers[q.id] ?? '').trim();
    if (v === '') {
      if (q.required) out[q.id] = 'required';
      continue; // an optional blank is an answer: there is no shape to check
    }
    if (q.inputMode === 'tel' && !phoneLooksComplete(v)) out[q.id] = 'phone';
    else if (q.inputMode === 'email' && !emailLooksComplete(v)) out[q.id] = 'email';
  }
  return out;
}
